// IE Hub Support & FAQ Data
// Original generation: v2 IE Hub Help & Support Content Master Document
// Manually synced to v5 (FaqAndArticles/v5IE Hub Help & Support Content Master Document (4).md) on 2026-04-21
// Source of truth: Deliverable 3 (FAQ Section Inventories) + Deliverable 6 (Articles)

export interface SupportCategory {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  displayOrder: number;
  faqList: string[];
  relatedFromOtherTopics: { faqTitle: string; fromSection: string; targetFaqId?: string }[];
}

export interface SupportFaq {
  id: string;
  categoryId: string;
  title: string;
  content: string;
  showOnHome: boolean;
  homeDisplayOrder?: number;
  homeIcon?: string;
  isSecondary?: boolean;
  relatedFaqIds: string[];
  learnMore: { articleId: string; title: string }[];
  searchKeywords: string[];
}

export interface SupportArticle {
  id: string;
  type: string;
  title: string;
  subtitle: string;
  content: string;
}

export interface SearchConfig {
  placeholderText: string;
  popularSearchTags: string[];
  noResultsMessage: string;
  synonymMap: Record<string, string>;
}

export interface EscalationConfig {
  preCTACopy: string;
  ctaButtonText: string;
  channel: string;
  availability: string;
  outOfHoursMessage: string;
}

export interface FeedbackConfig {
  promptText: string;
  positiveOption: string;
  negativeOption: string;
  followUpOnNo: string;
}

export const CATEGORIES: SupportCategory[] = [
  {
    id: 'SEC-LOGIN',
    title: 'Logging in & my account',
    subtitle: 'Can’t get in? Not receiving your link? Having technical issues?',
    icon: 'login',
    displayOrder: 1,
    faqList: [
      'How do I log in?',
      'I haven’t received my login email',
      'The page isn’t loading properly',
      'I’m stuck or can’t move forward',
      'Can I change my email address?',
      'The page isn’t working or keeps crashing - what should I do?',
    ],
    relatedFromOtherTopics: [
      { faqTitle: 'How do I create my budget?', fromSection: 'SEC-BUDGET', targetFaqId: 'FAQ-BUDGET-001' },
      { faqTitle: 'How do I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-001' },
      { faqTitle: 'Can I speak to someone?', fromSection: 'SEC-ABOUT', targetFaqId: 'FAQ-ABOUT-003' },
    ],
  },
  {
    id: 'SEC-SHARING',
    title: 'Sharing my budget',
    subtitle: 'How to send your budget and what happens after',
    icon: 'share',
    displayOrder: 2,
    faqList: [
      'How do I share my budget?',
      'What happens after I share my budget?',
      'How do I download or email my budget?',
      'Why hasn’t the company contacted me yet?',
      'Why can’t I find a company to share with?',
      'Can I share my budget with more than one company?',
      'A company asked me to send my budget by email, should I?',
    ],
    relatedFromOtherTopics: [
      { faqTitle: 'Can IE Hub set up a payment plan for me?', fromSection: 'SEC-PAYMENTS', targetFaqId: 'FAQ-PAY-001' },
      { faqTitle: 'How do I update my budget?', fromSection: 'SEC-BUDGET', targetFaqId: 'FAQ-BUDGET-004' },
      { faqTitle: 'Can I upload documents?', fromSection: 'SEC-DOC', targetFaqId: 'FAQ-DOC-001' },
    ],
  },
  {
    id: 'SEC-BUDGET',
    title: 'Creating & updating my budget',
    subtitle: 'Filling in your details, making changes, and understanding your budget',
    icon: 'budget',
    displayOrder: 3,
    faqList: [
      'How do I create my budget?',
      'Why am I being asked so many questions?',
      'Do I need exact figures?',
      'How do I update my budget?',
      'Do I need to include all my debts?',
      'I don’t know some of the answers — what should I do?',
      'What happens if I make a mistake?',
      'Do I have to complete everything?',
      'What happens if I don’t complete my budget?',
      'Why does my budget not match what I actually have left?',
      'Can I update my budget after I’ve sent it?',
    ],
    relatedFromOtherTopics: [
      { faqTitle: 'How do I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-001' },
      { faqTitle: 'What happens after I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-002' },
      { faqTitle: 'Can IE Hub set up a payment plan for me?', fromSection: 'SEC-PAYMENTS', targetFaqId: 'FAQ-PAY-001' },
    ],
  },
  {
    id: 'SEC-DOC',
    title: 'Uploading documents',
    subtitle: 'Sending ID, proof of income, and supporting documents',
    icon: 'documents',
    displayOrder: 4,
    faqList: [
      'How do I upload documents?',
      'What documents do I need to provide?',
      'Can I upload documents from my phone?',
      'Can I share documents with multiple companies?',
    ],
    relatedFromOtherTopics: [
      { faqTitle: 'How do I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-001' },
      { faqTitle: 'What happens after I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-002' },
      { faqTitle: 'Can companies see my information?', fromSection: 'SEC-PRIVACY', targetFaqId: 'FAQ-PRIVACY-002' },
    ],
  },
  {
    id: 'SEC-PAYMENTS',
    title: 'Payments & support',
    subtitle: 'What IE Hub can and cannot do with your payments',
    icon: 'payments',
    displayOrder: 5,
    faqList: [
      'Can IE Hub set up a payment plan for me?',
      'Can IE Hub help me with my payments?',
      'I need help urgently - what should I do?',
    ],
    relatedFromOtherTopics: [
      { faqTitle: 'What happens after I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-002' },
      { faqTitle: 'Can I speak to someone?', fromSection: 'SEC-ABOUT', targetFaqId: 'FAQ-ABOUT-003' },
      { faqTitle: 'Can IE Hub give me a fuel voucher?', fromSection: 'SEC-ABOUT', targetFaqId: 'FAQ-ABOUT-004' },
    ],
  },
  {
    id: 'SEC-ABOUT',
    title: 'About IE Hub',
    subtitle: 'What IE Hub does and how it works',
    icon: 'about',
    displayOrder: 6,
    faqList: [
      'What is IE Hub?',
      'Why have I been asked to use IE Hub?',
      'Can I speak to someone?',
      'Can IE Hub give me a fuel voucher?',
      'Why am I receiving emails from IE Hub?',
      'Can IE Hub help me apply for benefits?',
      'Can you tell me my reference number?',
      'I’m struggling with my finances - can you help?',
      'Can IE Hub give me financial advice?',
      'I’m worried about sharing my information - what should I do?',
    ],
    relatedFromOtherTopics: [
      { faqTitle: 'How do I create my budget?', fromSection: 'SEC-BUDGET', targetFaqId: 'FAQ-BUDGET-001' },
      { faqTitle: 'How do I log in?', fromSection: 'SEC-LOGIN', targetFaqId: 'FAQ-LOGIN-001' },
      { faqTitle: 'How do I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-001' },
    ],
  },
  {
    id: 'SEC-PRIVACY',
    title: 'Privacy & security',
    subtitle: 'How your data is protected and who can see it',
    icon: 'privacy',
    displayOrder: 7,
    faqList: [
      'Is my information secure?',
      'Who can see my budget?',
      'Can companies see my bank transactions?',
      'Will you sell my data?',
      'Do you use my data to train AI?',
      'How long do you keep my data for?',
    ],
    relatedFromOtherTopics: [
      { faqTitle: 'How do I share my budget?', fromSection: 'SEC-SHARING', targetFaqId: 'FAQ-SHARE-001' },
      { faqTitle: 'Can I upload documents?', fromSection: 'SEC-DOC', targetFaqId: 'FAQ-DOC-001' },
      { faqTitle: 'What is IE Hub?', fromSection: 'SEC-ABOUT', targetFaqId: 'FAQ-ABOUT-001' },
    ],
  },
];

export const FAQS: SupportFaq[] = [
  {
    id: 'FAQ-LOGIN-001',
    categoryId: 'SEC-LOGIN',
    title: 'How do I log in?',
    content: `You can log in using the secure link sent to your email address.

Enter your email on the login page, then open your email and click the link provided. This will take you straight into your account.

If you haven’t received the email, check your spam or junk folder. If you're still having issues, try using a different browser or opening IE Hub in incognito/private mode.`,
    showOnHome: true,
    homeDisplayOrder: 4,
    homeIcon: 'login',
    relatedFaqIds: ['FAQ-LOGIN-002', 'FAQ-LOGIN-003'],
    learnMore: [
      { articleId: 'ART-LOGIN-001', title: 'Troubleshooting login issues' },
      { articleId: 'ART-TECH-001', title: 'Fixing login and technical issues' },
    ],
    searchKeywords: ['how do i log in', 'log'],
  },
  {
    id: 'FAQ-LOGIN-002',
    categoryId: 'SEC-LOGIN',
    title: 'I haven’t received my login email',
    content: `If you haven’t received your login email, there are a few things you can check:

* Check your spam or junk folder  
* Make sure you entered the correct email address  
* Wait a few minutes in case of a delay

If you still haven’t received it, try requesting a new login link.

If the issue continues, you may need to contact support.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-LOGIN-001', 'FAQ-LOGIN-003'],
    learnMore: [
      { articleId: 'ART-LOGIN-001', title: 'Troubleshooting login issues' },
    ],
    searchKeywords: ['email', 'haven', 'haven t', 'i haven’t received my login email', 'login', 'login email', 'received', 'received login', 't received'],
  },
  {
    id: 'FAQ-LOGIN-003',
    categoryId: 'SEC-LOGIN',
    title: 'The page isn’t loading properly',
    content: `If IE Hub isn’t loading properly, try the following:

* Use a different browser (such as Chrome, Edge, or Safari)
* Open IE Hub in private or incognito mode
* Clear your browser cache and cookies
* Check your internet connection
* If you're experiencing issues with webpages loading and you're using a VPN or Ad blocker, try disabling them to see if this resolves the problem

If the problem continues, try using a different device.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-LOGIN-001', 'FAQ-LOGIN-002'],
    learnMore: [
      { articleId: 'ART-LOGIN-001', title: 'Troubleshooting login issues' },
    ],
    searchKeywords: ['isn', 'isn t', 'loading', 'loading properly', 'page', 'page isn', 'properly', 't loading', 'the page isn’t loading properly', 'vpn', 'ad blocker', 'adblocker'],
  },
  {
    id: 'FAQ-LOGIN-004',
    categoryId: 'SEC-LOGIN',
    title: 'I’m stuck or can’t move forward',
    content: `If you’re unable to move forward, it may be because something is missing or incomplete.

Check that:

* All required fields have been filled in  
* You’ve saved your progress  
* You’ve followed the steps on the page

You can also try refreshing the page or logging in again.

If you’re still unable to continue, you may need to contact support.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-LOGIN-003', 'FAQ-BUDGET-008'],
    learnMore: [
      { articleId: 'ART-LOGIN-001', title: 'Troubleshooting login issues' },
    ],
    searchKeywords: ['forward', 'i’m stuck or can’t move forward', 'm stuck', 'move', 'move forward', 'stuck', 'stuck t', 't move'],
  },
  {
    id: 'FAQ-LOGIN-005',
    categoryId: 'SEC-LOGIN',
    title: 'Can I change my email address?',
    content: `If you need to change your email address, you may need support to update this for you.

Your email address is used to securely access your account, so changes must be handled carefully.

If your email address is no longer accessible, please contact support for help.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-LOGIN-001', 'FAQ-ABOUT-003'],
    learnMore: [
      { articleId: 'ART-SUPPORT-001', title: 'Where to get help and support' },
    ],
    searchKeywords: ['address', 'can i change my email address', 'change', 'change email', 'email', 'email address'],
  },
  {
    id: 'FAQ-LOGIN-006',
    categoryId: 'SEC-LOGIN',
    title: 'The page isn’t working or keeps crashing - what should I do?',
    content: `Try opening IE Hub in a different browser or using incognito/private mode.

This clears stored data like cookies and can fix most loading issues.

If the problem continues, try using a different device or internet connection.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-LOGIN-001', 'FAQ-LOGIN-002'],
    learnMore: [
      { articleId: 'ART-TECH-001', title: 'Fixing browser and loading issues' },
    ],
    searchKeywords: ['crashing', 'isn', 'isn t', 'keeps', 'keeps crashing', 'page', 'page isn', 't working', 'the page isn’t working or keeps crashing - what should i do', 'working', 'working keeps'],
  },
  {
    id: 'FAQ-SHARE-001',
    categoryId: 'SEC-SHARING',
    title: 'How do I share my budget?',
    content: `You can share your budget directly through your IE Hub account.

* Log in using your secure link  
* Go to your dashboard  
* Select your completed budget  
* Choose the company you want to share with  
* Click “Share securely”

This sends your budget safely to the company.

If the company is not listed, you can download your budget and send it to them if they have asked you to.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-SHARE-002', 'FAQ-SHARE-003'],
    learnMore: [
      { articleId: 'ART-SHARING-001', title: 'Sharing your budget explained' },
      { articleId: 'ART-NEXT-STEPS-001', title: 'What to expect after sharing your budget' },
    ],
    searchKeywords: ['budget', 'how do i share my budget', 'share', 'share budget'],
  },
  {
    id: 'FAQ-SHARE-002',
    categoryId: 'SEC-SHARING',
    title: 'What happens after I share my budget?',
    content: `The company you shared your budget with will review it and contact you.

This usually happens within **7–10 working days**, although it may be sooner depending on the company.

IE Hub cannot access your account with that company or provide updates, so if you need to speak to them sooner, you should contact the company directly.`,
    showOnHome: true,
    homeDisplayOrder: 5,
    homeIcon: 'progress',
    relatedFaqIds: ['FAQ-PAY-001', 'FAQ-SHARE-003'],
    learnMore: [
      { articleId: 'ART-CREDITORS-001', title: 'How IE Hub works with companies' },
      { articleId: 'ART-NEXT-STEPS-001', title: 'What to expect after sharing your budget' },
    ],
    searchKeywords: ['after', 'after share', 'budget', 'happens', 'happens after', 'share', 'share budget', 'what happens after i share my budget'],
  },
  {
    id: 'FAQ-SHARE-003',
    categoryId: 'SEC-SHARING',
    title: 'How do I download or email my budget?',
    content: `You can download a copy of your budget from your IE Hub account at any time.

To download your budget:

* Log in to your account  
* Go to your dashboard  
* Select your completed budget  
* Click the option to download or print your budget  
* Save it as a PDF on your device

Once downloaded, you can email it to a company if they have asked you to send it.

If the company is available on IE Hub, the easiest and most secure option is to share your budget directly through your account. In some cases, Freepost may be available depending on the company.

Always make sure you are sending your budget to the correct email address provided by the company.`,
    showOnHome: true,
    homeDisplayOrder: 3,
    homeIcon: 'share',
    relatedFaqIds: ['FAQ-SHARE-001', 'FAQ-SHARE-002'],
    learnMore: [
      { articleId: 'ART-SHARING-001', title: 'Sharing your budget explained' },
      { articleId: 'ART-DOCUMENTS-001', title: 'Uploading and sending documents' },
    ],
    searchKeywords: ['budget', 'download', 'download email', 'email', 'email budget', 'how do i download or email my budget'],
  },
  {
    id: 'FAQ-SHARE-004',
    categoryId: 'SEC-SHARING',
    title: 'Why hasn’t the company contacted me yet?',
    content: `It can take up to **7–10 working days** for a company to review your budget and contact you.

If you need to speak to them sooner, you should contact the company directly using the details they’ve provided.

IE Hub cannot provide updates on your account with that company.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-SHARE-002', 'FAQ-PAY-001'],
    learnMore: [
      { articleId: 'ART-NEXT-STEPS-001', title: 'What to expect after sharing your budget' },
    ],
    searchKeywords: ['company', 'company contacted', 'contacted', 'contacted yet', 'hasn', 'hasn t', 't company', 'why hasn’t the company contacted me yet', 'yet'],
  },
  {
    id: 'FAQ-SHARE-005',
    categoryId: 'SEC-SHARING',
    title: 'Why can’t I find a company to share with?',
    content: `Not all companies are available to share with directly on IE Hub.

If you can’t find the company:

* Check the name you are searching for  
* Try using a shorter or more general name

If the company is still not listed, you can download your budget and send it to them if they have asked you to.

In some cases, Freepost may also be available depending on the company.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-SHARE-001', 'FAQ-SHARE-003'],
    learnMore: [
      { articleId: 'ART-SHARING-001', title: 'Sharing your budget explained' },
    ],
    searchKeywords: ['company', 'company share', 'find', 'find company', 'share', 't find', 'why can’t i find a company to share with'],
  },
  {
    id: 'FAQ-SHARE-006',
    categoryId: 'SEC-SHARING',
    title: 'Can I share my budget with more than one company?',
    content: `Yes, you can share your budget with more than one company.

You can select and share your budget with each company separately through your IE Hub account.

Each company will only see the version you choose to share with them.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-SHARE-001', 'FAQ-SHARE-002'],
    learnMore: [
      { articleId: 'ART-SHARING-001', title: 'Sharing your budget explained' },
    ],
    searchKeywords: ['budget', 'budget more', 'can i share my budget with more than one company', 'company', 'more', 'more one', 'one', 'one company', 'share', 'share budget'],
  },
  {
    id: 'FAQ-SHARE-007',
    categoryId: 'SEC-SHARING',
    title: 'A company asked me to send my budget by email, should I?',
    content: `If the company is available on IE Hub, the easiest and most secure way to share your budget is directly through your account.

If the company is not available, you can download your budget and email it to them if they have asked you to send it.

In some cases, Freepost may also be available depending on the company.

Always make sure you are sending your budget to the correct email address provided by the company.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-SHARE-003', 'FAQ-SHARE-001'],
    learnMore: [
      { articleId: 'ART-SHARING-001', title: 'Sharing your budget explained' },
    ],
    searchKeywords: ['a company asked me to send my budget by email, should i', 'asked', 'asked send', 'budget', 'budget email', 'company', 'company asked', 'email', 'send', 'send budget'],
  },
  {
    id: 'FAQ-BUDGET-001',
    categoryId: 'SEC-BUDGET',
    title: 'How do I create my budget?',
    content: `You can create your budget by following the steps in your IE Hub account.

After logging in:

* Start by entering your income  
* Add your regular spending and bills  
* Include any debts or financial commitments

Your progress is saved as you go, so you can come back at any time.

Once complete, you can share your budget with a company.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-003', 'FAQ-SHARE-001'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['budget', 'create', 'create budget', 'how do i create my budget'],
  },
  {
    id: 'FAQ-BUDGET-002',
    categoryId: 'SEC-BUDGET',
    title: 'Why am I being asked so many questions?',
    content: `You are being asked these questions so your budget gives a clear and accurate picture of your finances.

Companies use this information to understand your situation and decide what support they can offer.

Answering all sections helps avoid delays and ensures they have everything they need.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-003', 'FAQ-BUDGET-008'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['asked', 'asked many', 'being', 'being asked', 'many', 'many questions', 'questions', 'why am i being asked so many questions'],
  },
  {
    id: 'FAQ-BUDGET-003',
    categoryId: 'SEC-BUDGET',
    title: 'Do I need exact figures?',
    content: `No, you do not need exact figures.

You can use estimates based on your usual income and spending.

It’s more important that your budget reflects your overall situation than being perfectly exact.

You can update your budget later if anything changes.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-006', 'FAQ-BUDGET-004'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['do i need exact figures', 'exact', 'exact figures', 'figures'],
  },
  {
    id: 'FAQ-BUDGET-004',
    categoryId: 'SEC-BUDGET',
    title: 'How do I update my budget?',
    content: `You can update your budget at any time.

* Log in to your account  
* Go to your dashboard  
* Edit the section that needs changing  
* Save your updates

If you have already shared your budget, you will need to re-share it so companies receive the latest version.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-011', 'FAQ-BUDGET-007'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['budget', 'how do i update my budget', 'update', 'update budget'],
  },
  {
    id: 'FAQ-BUDGET-005',
    categoryId: 'SEC-BUDGET',
    title: 'Do I need to include all my debts?',
    content: `Yes, you should include all your debts where possible.

This helps give a full and accurate picture of your financial situation.

If debts are missing, the company may not fully understand your circumstances or offer the right support.

If you’re unsure, you can use your best estimate and update it later.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-003', 'FAQ-BUDGET-006'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['debts', 'do i need to include all my debts', 'include', 'include debts'],
  },
  {
    id: 'FAQ-BUDGET-006',
    categoryId: 'SEC-BUDGET',
    title: 'I don’t know some of the answers — what should I do?',
    content: `If you don’t know some of the answers, you can use your best estimate.

Your budget is designed to give an overall picture of your finances, so it’s okay if some figures are not exact.

You can update your budget at any time if you find more accurate information later.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-003', 'FAQ-BUDGET-004'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['answers', 'don', 'don t', 'i don’t know some of the answers — what should i do', 'know', 'know answers', 't know'],
  },
  {
    id: 'FAQ-BUDGET-007',
    categoryId: 'SEC-BUDGET',
    title: 'What happens if I make a mistake?',
    content: `If you make a mistake, you can update your budget at any time.

* Log in to your account
* Go to your dashboard
* Edit the section that needs changing
* Save your updates

If you have already shared your budget, you may need to re-share it so companies receive the updated version.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-004', 'FAQ-BUDGET-011'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['happens', 'happens make', 'make', 'make mistake', 'mistake', 'what happens if i make a mistake'],
  },
  {
    id: 'FAQ-BUDGET-008',
    categoryId: 'SEC-BUDGET',
    title: 'Do I have to complete everything?',
    content: `You should complete as much of your budget as possible.

Providing more information helps give a clearer picture of your financial situation and can help companies review your case more quickly.

If something is missing, you may still be able to continue, but the company may need more information before they can make a decision.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-006', 'FAQ-BUDGET-002'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['complete', 'complete everything', 'do i have to complete everything', 'everything'],
  },
  {
    id: 'FAQ-BUDGET-009',
    categoryId: 'SEC-BUDGET',
    title: 'What happens if I don’t complete my budget?',
    content: `If you don’t complete your budget, you won’t be able to share it with a company.

This means they may not have enough information to understand your situation or offer support.

You can log back in at any time to continue and complete your budget.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-001', 'FAQ-BUDGET-008'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['budget', 'complete', 'complete budget', 'don', 'don t', 'happens', 'happens don', 't complete', 'what happens if i don’t complete my budget'],
  },
  {
    id: 'FAQ-BUDGET-010',
    categoryId: 'SEC-BUDGET',
    title: 'Why does my budget not match what I actually have left?',
    content: `Your budget reflects the information that has been entered.

If your budget doesn’t match what you actually have left, it may be because:

* your situation has changed
* some information needs updating
* something may have been missed or entered differently

You can check this by going to **Preview your budget** and reviewing each section.

If anything needs updating, you can edit your budget at any time so it reflects your current finances.

Keeping your budget up to date helps companies understand your situation accurately.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-004', 'FAQ-BUDGET-006'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['actually', 'actually left', 'budget', 'budget match', 'left', 'match', 'match actually', 'why does my budget not match what i actually have left'],
  },
  {
    id: 'FAQ-BUDGET-011',
    categoryId: 'SEC-BUDGET',
    title: 'Can I update my budget after I’ve sent it?',
    content: `Yes. You can update your budget at any time.

If your situation changes, edit your details and save your updates.

If you’ve already shared your budget, you will need to re-share it so companies receive the latest version.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-004', 'FAQ-SHARE-002'],
    learnMore: [
      { articleId: 'ART-BUDGET-001', title: 'Completing and updating your budget' },
    ],
    searchKeywords: ['after', 'after ve', 'budget', 'budget after', 'can i update my budget after i’ve sent it', 'sent', 'update', 'update budget', 've sent'],
  },
  {
    id: 'FAQ-DOC-001',
    categoryId: 'SEC-DOC',
    title: 'How do I upload documents?',
    content: `You can upload documents directly within your IE Hub account.

* Log in to your account  
* Go to the relevant section  
* Select the option to upload documents  
* Choose the file from your device

Make sure the document is clear and easy to read before uploading.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-DOC-002', 'FAQ-DOC-003'],
    learnMore: [
      { articleId: 'ART-DOCUMENTS-001', title: 'Uploading and sending documents' },
    ],
    searchKeywords: ['documents', 'how do i upload documents', 'upload', 'upload documents'],
  },
  {
    id: 'FAQ-DOC-002',
    categoryId: 'SEC-DOC',
    title: 'What documents do I need to provide?',
    content: `The documents you need will depend on what the company has asked for.

This may include:

* Proof of income  
* Bank statements  
* Identification

Always follow the instructions provided by the company, as requirements can vary.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-DOC-001', 'FAQ-DOC-004'],
    learnMore: [
      { articleId: 'ART-DOCUMENTS-001', title: 'Uploading and sending documents' },
    ],
    searchKeywords: ['documents', 'documents provide', 'provide', 'what documents do i need to provide'],
  },
  {
    id: 'FAQ-DOC-003',
    categoryId: 'SEC-DOC',
    title: 'Can I upload documents from my phone?',
    content: `Yes, you can upload documents from your phone.

You can either:

* Take a photo of the document  
* Upload an existing file

Make sure the image is clear and all details are visible before uploading.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-DOC-001', 'FAQ-DOC-002'],
    learnMore: [
      { articleId: 'ART-DOCUMENTS-001', title: 'Uploading and sending documents' },
    ],
    searchKeywords: ['can i upload documents from my phone', 'documents', 'documents phone', 'phone', 'upload', 'upload documents'],
  },
  {
    id: 'FAQ-DOC-004',
    categoryId: 'SEC-DOC',
    title: 'Can I share documents with multiple companies?',
    content: `Yes, you can share documents with more than one company if needed.

You will need to upload or share the documents for each company as required.

Each company will only see the documents you choose to share with them.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-DOC-001', 'FAQ-SHARE-006'],
    learnMore: [
      { articleId: 'ART-DOCUMENTS-001', title: 'Uploading and sending documents' },
    ],
    searchKeywords: ['can i share documents with multiple companies', 'companies', 'documents', 'documents multiple', 'multiple', 'multiple companies', 'share', 'share documents'],
  },
  {
    id: 'FAQ-PAY-001',
    categoryId: 'SEC-PAYMENTS',
    title: 'Can IE Hub set up a payment plan for me?',
    content: `No, IE Hub cannot set up payment plans.

IE Hub allows you to create a budget and share it with companies so they can understand your financial situation. The company you share your budget with will review it and contact you to discuss any payment arrangements.

If you need to speak to them sooner, you should contact the company directly.`,
    showOnHome: true,
    homeDisplayOrder: 2,
    homeIcon: 'payments',
    relatedFaqIds: ['FAQ-SHARE-002', 'FAQ-PAY-002'],
    learnMore: [
      { articleId: 'ART-CREDITORS-001', title: 'How IE Hub works with companies' },
      { articleId: 'ART-SHARING-001', title: 'Sharing your budget explained' },
    ],
    searchKeywords: ['can ie hub set up a payment plan for me', 'hub', 'hub set', 'ie hub', 'payment', 'payment plan', 'plan', 'set', 'set payment'],
  },
  {
    id: 'FAQ-PAY-002',
    categoryId: 'SEC-PAYMENTS',
    title: 'Can IE Hub help me with my payments?',
    content: `No, IE Hub cannot manage or take payments.

IE Hub helps you organise your financial information so companies can understand your situation.

The company you are dealing with will handle any payment arrangements directly with you.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PAY-001', 'FAQ-SHARE-002'],
    learnMore: [
      { articleId: 'ART-CREDITORS-001', title: 'How IE Hub works with companies' },
    ],
    searchKeywords: ['can ie hub help me with my payments', 'help', 'help payments', 'hub', 'hub help', 'ie hub', 'payments'],
  },
  {
    id: 'FAQ-PAY-003',
    categoryId: 'SEC-PAYMENTS',
    title: 'I need help urgently — what should I do?',
    content: `If you need urgent help, you should contact the company you are dealing with directly.

They can discuss your situation and provide support as quickly as possible.

You may also want to contact a free debt advice organisation such as:

* National Debtline  
* StepChange Debt Charity

These organisations can offer free, confidential support and guidance.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PAY-002', 'FAQ-ABOUT-008'],
    learnMore: [
      { articleId: 'ART-SUPPORT-001', title: 'Where to get help and support' },
    ],
    searchKeywords: ['help', 'help urgently', 'i need help urgently — what should i do', 'urgently'],
  },
  {
    id: 'FAQ-ABOUT-001',
    categoryId: 'SEC-ABOUT',
    title: 'What is IE Hub?',
    content: `IE Hub is a platform that helps you create a budget showing your income and spending.

You can securely share this with companies so they can understand your financial situation without needing a phone call.

You can update your budget at any time if your situation changes.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-001', 'FAQ-SHARE-001'],
    learnMore: [
      { articleId: 'ART-CREDITORS-001', title: 'How IE Hub works with companies' },
    ],
    searchKeywords: ['hub', 'ie hub', 'what is ie hub'],
  },
  {
    id: 'FAQ-ABOUT-002',
    categoryId: 'SEC-ABOUT',
    title: 'Why have I been asked to use IE Hub?',
    content: `You’ve been asked to use IE Hub so a company can understand your financial situation.

By completing your budget, you give them a clear picture of your income and spending, which helps them decide what support they can offer.

Providing this information can help them review your situation more quickly.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-BUDGET-001', 'FAQ-SHARE-002'],
    learnMore: [
      { articleId: 'ART-CREDITORS-001', title: 'How IE Hub works with companies' },
    ],
    searchKeywords: ['asked', 'asked use', 'been', 'been asked', 'hub', 'ie hub', 'use', 'use ie', 'why have i been asked to use ie hub'],
  },
  {
    id: 'FAQ-ABOUT-003',
    categoryId: 'SEC-ABOUT',
    title: 'Can I speak to someone?',
    content: `IE Hub does not offer phone support.

IE Hub is an online service, so everything is completed through your account. If you need help with your budget or a technical issue, you can visit our help centre.

If you need advice about your finances, you should contact the company you are dealing with or a free debt advice organisation.

Organisations such as:

* National Debtline  
* StepChange Debt Charity

can offer free, confidential support and guidance.`,
    showOnHome: true,
    homeDisplayOrder: 1,
    homeIcon: 'support',
    relatedFaqIds: ['FAQ-PAY-003', 'FAQ-ABOUT-004'],
    learnMore: [
      { articleId: 'ART-SUPPORT-001', title: 'Where to get help and support' },
    ],
    searchKeywords: ['can i speak to someone', 'someone', 'speak', 'speak someone'],
  },
  {
    id: 'FAQ-ABOUT-004',
    categoryId: 'SEC-ABOUT',
    title: 'Can IE Hub give me a fuel voucher?',
    content: `No, IE Hub does not provide fuel vouchers or financial support directly.

IE Hub helps you share your financial information with companies so they can understand your situation and decide what support they may offer.

If you need help, you should contact the company directly or a support organisation.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PAY-003', 'FAQ-ABOUT-003'],
    learnMore: [
      { articleId: 'ART-SUPPORT-001', title: 'Where to get help and support' },
    ],
    searchKeywords: ['can ie hub give me a fuel voucher', 'fuel', 'fuel voucher', 'give', 'give fuel', 'hub', 'hub give', 'ie hub', 'voucher'],
  },
  {
    id: 'FAQ-ABOUT-005',
    categoryId: 'SEC-ABOUT',
    title: 'Why am I receiving emails from IE Hub?',
    content: `You are receiving emails because a company has asked you to use IE Hub.

These emails help you access your account and complete your budget.

If you are unsure, check the email for details of the company who referred you.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-LOGIN-001', 'FAQ-ABOUT-001'],
    learnMore: [
      { articleId: 'ART-CREDITORS-001', title: 'How IE Hub works with companies' },
    ],
    searchKeywords: ['emails', 'emails ie', 'hub', 'ie hub', 'receiving', 'receiving emails', 'why am i receiving emails from ie hub'],
  },
  {
    id: 'FAQ-ABOUT-006',
    categoryId: 'SEC-ABOUT',
    title: 'Can IE Hub help me apply for benefits?',
    content: `IE Hub cannot apply for benefits on your behalf.

However, you can use the benefits checker in your account to see what you may be eligible for based on your information.

If you need help applying, you should contact a benefits advice organisation.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-ABOUT-009', 'FAQ-ABOUT-001'],
    learnMore: [
      { articleId: 'ART-BENEFITS-001', title: 'Using the benefits checker' },
    ],
    searchKeywords: ['apply', 'apply benefits', 'benefits', 'can ie hub help me apply for benefits', 'help', 'help apply', 'hub', 'hub help', 'ie hub'],
  },
  {
    id: 'FAQ-ABOUT-007',
    categoryId: 'SEC-ABOUT',
    title: 'Can you tell me my reference number?',
    content: `IE Hub does not provide reference numbers to customers.

If a company has given you a reference number, it will come directly from them. You may need to check their emails or letters for this information.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-SHARE-001', 'FAQ-ABOUT-003'],
    learnMore: [
      { articleId: 'ART-CREDITORS-001', title: 'How IE Hub works with companies' },
    ],
    searchKeywords: ['can you tell me my reference number', 'number', 'reference', 'reference number', 'tell', 'tell reference'],
  },
  {
    id: 'FAQ-ABOUT-008',
    categoryId: 'SEC-ABOUT',
    title: 'I’m struggling with my finances - can you help?',
    content: `I’m sorry you’re going through that - you’re not alone in feeling this way.

IE Hub can help you organise your income and spending so companies can better understand your situation.

If you need advice or support, you should contact a **free debt advice organisation** or the company you’re dealing with directly, as IE Hub cannot provide financial advice.

Organisations such as:

* [**National Debtline**](https://nationaldebtline.org/)  
* [**StepChange Debt Charity**](https://www.stepchange.org/)

can offer free, confidential support and guidance.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PAY-001', 'FAQ-ABOUT-006'],
    learnMore: [
      { articleId: 'ART-SUPPORT-001', title: 'Where to get help and support' },
    ],
    searchKeywords: ['finances', 'finances help', 'help', 'i’m struggling with my finances - can you help', 'm struggling', 'struggling', 'struggling finances'],
  },
  {
    id: 'FAQ-ABOUT-009',
    categoryId: 'SEC-ABOUT',
    title: 'Can IE Hub give me financial advice?',
    content: `No, IE Hub does not provide financial advice.

IE Hub helps you create and share your budget so companies can understand your financial situation.

If you need advice about debt, benefits, or managing your finances, you should contact a free advice organisation or the company you are dealing with directly.

Organisations such as:  
 National Debtline  
 StepChange Debt Charity

can offer free, confidential support and guidance.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PAY-001', 'FAQ-ABOUT-003'],
    learnMore: [
      { articleId: 'ART-SUPPORT-001', title: 'Where to get help and support' },
    ],
    searchKeywords: ['advice', 'can ie hub give me financial advice', 'financial', 'financial advice', 'give', 'give financial', 'hub', 'hub give', 'ie hub'],
  },
  {
    id: 'FAQ-ABOUT-010',
    categoryId: 'SEC-ABOUT',
    title: 'I’m worried about sharing my information - what should I do?',
    content: `It’s completely understandable to feel unsure about sharing personal information.

You are always in control of what you share and who you share it with. Nothing is sent to any company unless you choose to share your budget.

Sharing your budget can help companies understand your situation and offer the right support.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PRIVACY-001', 'FAQ-PRIVACY-002'],
    learnMore: [
      { articleId: 'ART-PRIVACY-001', title: 'How your data is protected' },
    ],
    searchKeywords: ['about', 'about sharing', 'information', 'i’m worried about sharing my information - what should i do', 'm worried', 'sharing', 'sharing information', 'worried', 'worried about'],
  },
  {
    id: 'FAQ-PRIVACY-001',
    categoryId: 'SEC-PRIVACY',
    title: 'Is my information secure?',
    content: `Yes, your information is secure.

Your data is encrypted and stored safely, and no one can see your information unless you choose to share your budget with a company.

IE Hub cannot access your data unless it is required for support and you have given permission.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PRIVACY-002', 'FAQ-PRIVACY-003'],
    learnMore: [
      { articleId: 'ART-PRIVACY-001', title: 'How your data is protected' },
    ],
    searchKeywords: ['information', 'information secure', 'is my information secure', 'secure'],
  },
  {
    id: 'FAQ-PRIVACY-002',
    categoryId: 'SEC-PRIVACY',
    title: 'Who can see my budget?',
    content: `Only you and the companies you choose to share your budget with can see your information.

Nothing is shared automatically. You stay in control of who sees your budget and when it is shared.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PRIVACY-001', 'FAQ-SHARE-001'],
    learnMore: [
      { articleId: 'ART-PRIVACY-001', title: 'How your data is protected' },
    ],
    searchKeywords: ['budget', 'see', 'see budget', 'who can see my budget'],
  },
  {
    id: 'FAQ-PRIVACY-003',
    categoryId: 'SEC-PRIVACY',
    title: 'Can companies see my bank transactions?',
    content: `No, companies cannot see your bank transactions through IE Hub.

IE Hub does not give companies access to your bank account.

If you choose to use Open Banking, your transaction information can be used to help complete your budget, but this only happens with your permission.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PRIVACY-001', 'FAQ-PRIVACY-002'],
    learnMore: [
      { articleId: 'ART-OPENBANK-001', title: 'How Open Banking works' },
    ],
    searchKeywords: ['bank', 'bank transactions', 'can companies see my bank transactions', 'companies', 'companies see', 'see', 'see bank', 'transactions'],
  },
  {
    id: 'FAQ-PRIVACY-004',
    categoryId: 'SEC-PRIVACY',
    title: 'Will you sell my data?',
    content: `No, IE Hub does not sell your personal data.

Your information is only used to help you create and share your budget. It is only shared with companies if you choose to send it to them.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PRIVACY-001', 'FAQ-PRIVACY-002'],
    learnMore: [
      { articleId: 'ART-PRIVACY-001', title: 'How your data is protected' },
    ],
    searchKeywords: ['data', 'sell', 'sell data', 'will you sell my data'],
  },
  {
    id: 'FAQ-PRIVACY-005',
    categoryId: 'SEC-PRIVACY',
    title: 'Do you use my data to train AI?',
    content: `No, IE Hub does not use your personal financial information to train AI models.

Your data is only used to provide the service and help you manage your budget.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PRIVACY-004', 'FAQ-PRIVACY-001'],
    learnMore: [
      { articleId: 'ART-PRIVACY-001', title: 'How your data is protected' },
    ],
    searchKeywords: ['data', 'data train', 'do you use my data to train ai', 'train', 'train ai', 'use', 'use data'],
  },
  {
    id: 'FAQ-PRIVACY-006',
    categoryId: 'SEC-PRIVACY',
    title: 'How long do you keep my data for?',
    content: `IE Hub keeps your data only for as long as needed to provide the service and meet legal or regulatory requirements.

For full details, you can refer to IE Hub’s privacy policy.`,
    showOnHome: false,
    relatedFaqIds: ['FAQ-PRIVACY-001', 'FAQ-PRIVACY-004'],
    learnMore: [
      { articleId: 'ART-PRIVACY-001', title: 'How your data is protected' },
    ],
    searchKeywords: ['data', 'how long do you keep my data for', 'keep', 'keep data', 'long', 'long keep'],
  },
];

export const ARTICLES: SupportArticle[] = [
  {
    id: 'ART-CREDITORS-001',
    type: 'Explainer',
    title: 'How IE Hub works with companies',
    subtitle: 'What happens when you share your budget and how companies use it',
    content: `**This article explains what happens when you share your budget and what companies do with it.**

## **What IE Hub does**

IE Hub helps you create a budget that shows your income, spending, and debts.

When you share your budget, companies can see a clear picture of your financial situation so they can understand what you can afford and review your circumstances.

## **How companies use your budget**

Companies use your budget to:

* Understand your income and spending  
* Review what you can realistically afford  
* Decide what support or options they can offer  
* Identify if they need more information

**IE Hub has no influence over these decisions.**

## **What IE Hub does not do**

* IE Hub does not set up payment plans  
* IE Hub does not contact companies on your behalf  
* IE Hub does not make decisions about your account

## **What happens after you share your budget**

Once you share your budget, the company will review it and contact you.

This usually happens within **7–10 working days**, although it may be sooner depending on the company.

## **Working with companies after sharing**

After reviewing your budget, the company may:

* Contact you to discuss your situation  
* Ask for more information or documents  
* Talk through possible payment options or support

If your situation changes, you can update your budget and share it again so they have the most up-to-date information.

## **What to do if you need help sooner**

If you need to speak to someone urgently, contact the company directly.

**IE Hub cannot provide updates or make decisions about your account.**`,
  },
  {
    id: 'ART-SHARING-001',
    type: 'Guide',
    title: 'Sharing your budget explained',
    subtitle: 'A simple guide to sending your budget to a company',
    content: `**This guide shows you how to share your budget with a company using IE Hub.**

### **Step-by-step: share your budget**

1. **Log in to your account**  
    Use the secure link sent to your email address  
2. **Go to your dashboard**  
    This is where your budget is stored  
3. **Select a company**  
    Choose the company you want to share your budget with  
4. **Click “Share securely”**  
    This sends your budget to the selected company

### **Other ways to share your budget**

If the company is not available to share with directly, you still have other options:

* You can **download your budget and email it** if a company asks you to  
* In some cases, **Freepost may be available**, depending on the company

**The easiest and most secure option is always to share your budget directly through IE Hub where possible.**

### **What happens next**

The company will review your budget and contact you to discuss your situation.

### **What to do if you can’t find a company**

If the company is not listed:

* download your budget  
* email it to them using the details they’ve provided`,
  },
  {
    id: 'ART-BUDGET-001',
    type: 'Guide',
    title: 'Completing and updating your budget',
    subtitle: 'How to fill in your budget and keep it up to date',
    content: `**This guide explains how to complete your budget and keep it accurate over time.**

### **Completing and updating your budget**

When filling in your budget:

* Enter your income and regular spending  
* Include all expenses, even small ones  
* Use estimates if you’re unsure

**Your budget should reflect your real financial situation as closely as possible.**

### **Updating your budget**

You can update your budget at any time.

* Log in to your account  
* Edit the section that needs changing  
* Save your updates

### **Important**

**If you have already shared your budget, you will need to re-share it after making changes.**

### **What to do if you’re unsure**

If you don’t know exact figures:

* use your best estimate  
* update it later if anything changes`,
  },
  {
    id: 'ART-PRIVACY-001',
    type: 'Explainer',
    title: 'How your data is protected',
    subtitle: 'How IE Hub keeps your information safe and under your control',
    content: `**This article explains how your information is protected and who can see it.**

### **You are in control**

Only you decide who to share your budget with.

**Nothing is shared unless you choose to share your budget.**

### **How your data is protected**

* Your information is encrypted  
* Your data is stored securely  
* Access is restricted

### **What companies can see**

Companies can only see the information you choose to share with them.

### **What IE Hub does not do**

* **IE Hub does not sell your data**  
* **IE Hub does not access your bank account directly**  
* **IE Hub does not use your data to train AI models**

### **Open Banking**

If you choose to connect your bank account:

* transaction data can help complete your budget  
* this only happens with your permission

### **What to do if you’re unsure about sharing**

If you feel unsure:

* review your information before sharing  
* only share with trusted companies`,
  },
  {
    id: 'ART-TECH-001',
    type: 'Troubleshooting',
    title: 'Fixing login and technical issues',
    subtitle: 'What to do if IE Hub isn’t loading or working properly',
    content: `**This guide helps you fix common login and technical issues.**

### **Try these steps first**

1. **Use a different browser**  
    Try Chrome, Edge, or Safari  
2. **Open IE Hub in incognito/private mode**  
    This clears stored data like cookies and often fixes loading issues  
3. **Check your internet connection**  
    Make sure your connection is stable  
4. **Try a different device**  
    This can help identify if the issue is device-related

### **What to do if the problem continues**

If you’re still having issues:

* try again later  
* contact support for further help`,
  },
  {
    id: 'ART-OPENBANK-001',
    type: 'Explainer',
    title: 'How Open Banking works',
    subtitle: 'How your transaction data is used to help complete your budget',
    content: `**This article explains how Open Banking works and how it helps you complete your budget securely.**

## **What is Open Banking?**

Open Banking allows you to securely connect your bank account to IE Hub.

This can help automatically bring in your transaction information so parts of your budget can be completed more quickly and accurately.

## **How it works**

If you choose to use Open Banking:

1. You will be redirected to your bank’s secure login page  
2. You log in using your normal bank details  
3. You choose what information to share  
4. Your transaction data is securely shared with IE Hub

**You are always in control of whether you use Open Banking.**

## **What information is shared?**

Open Banking can provide:

* Recent transaction history  
* Income and spending patterns

This information is used to help build your budget.

**IE Hub does not have direct access to your bank account.**

## **Is it secure?**

Yes. Open Banking is regulated and uses secure encryption.

Your data is only shared with your permission and can only be used for the purpose you agreed to.

## **What to do if you don’t want to use Open Banking**

You can still complete your budget manually.

You can enter your income and spending yourself without connecting your bank account.`,
  },
  {
    id: 'ART-NEXT-STEPS-001',
    type: 'Guide',
    title: 'What to expect after sharing your budget',
    subtitle: 'What happens once you send your budget to a company',
    content: `**This guide explains what happens after you share your budget and what to expect next.**

## **What happens after you share your budget?**

Once you share your budget, the company will review your information.

They use this to understand your financial situation and decide what support they can offer.

## **How long does it take?**

Most companies will contact you within **7–10 working days**.

This may vary depending on the company and their processes.

## **How will they contact you?**

They may contact you by:

* Phone  
* Email  
* Letter

This depends on how they usually communicate with you.

## **What will they do next?**

The company may:

* Ask for more information  
* Request documents  
* Discuss payment options or support

**IE Hub has no influence over the outcome of this decision.**

## **What to do if you haven’t heard back**

If you haven’t been contacted:

* Wait up to 10 working days  
* Contact the company directly if needed

## **What to do if your situation changes**

If anything changes:

1. Update your budget  
2. Save your changes  
3. Share your budget again`,
  },
  {
    id: 'ART-DOCUMENTS-001',
    type: 'Guide',
    title: 'Uploading and sending documents',
    subtitle: 'How to provide documents to support your budget',
    content: `**This guide explains how to upload and send documents through IE Hub.**

## **Why documents may be needed**

A company may ask for documents to confirm your financial situation.

This helps them review your budget more accurately.

## **How to upload documents**

1. Log in to your account  
2. Go to the relevant section  
3. Select upload  
4. Choose your file

Make sure your document is clear and readable.

## **What documents you might need**

This may include:

* Proof of income  
* Bank statements  
* Identification

## **Using your phone**

You can:

* Take a photo  
* Upload an existing file

## **Sharing documents**

You can share documents with companies as requested.

**Each company will only see the documents you choose to share.**

## **What to do if you have trouble uploading**

* Try a different file format  
* Check your internet connection  
* Try another device`,
  },
  {
    id: 'ART-LOGIN-001',
    type: 'Troubleshooting',
    title: 'Troubleshooting login issues',
    subtitle: 'What to do if you can’t access your account',
    content: `**This guide helps you fix common login and access issues.**

## **I haven’t received my login email**

* Check spam or junk folder  
* Wait a few minutes  
* Request a new link

## **The page isn’t loading**

* Try another browser  
* Use private/incognito mode  
* Clear cache and cookies

## **I’m stuck or can’t move forward**

* Check all required fields are completed  
* Save your progress  
* Refresh the page

## **I need to change my email address**

Your email is used to securely access your account.

**You may need to contact support to update this.**

## **What to do if problems continue**

* Try a different device  
* Contact support if needed`,
  },
  {
    id: 'ART-BENEFITS-001',
    type: 'Guide',
    title: 'Using the benefits checker',
    subtitle: 'How to check what support you may be eligible for',
    content: `**This guide explains how to use the IE Hub benefits checker.**

## **What the benefits checker does**

The benefits checker helps you see what support you may be eligible for.

This can include:

* Government benefits  
* Water tariffs  
* Other support schemes

## **How it works**

The tool uses the information in your budget to identify possible support.

**It does not apply for benefits on your behalf.**

## **Why this is helpful**

It can highlight support you may not be aware of.

This can help improve your financial situation.

## **What to do next**

If you find something you may be eligible for:

* Follow the guidance provided  
* Contact the organisation directly

## **What to do if you need help applying**

You can contact:

* National Debtline  
* StepChange Debt Charity

They can provide free guidance.`,
  },
  {
    id: 'ART-SUPPORT-001',
    type: 'Explainer',
    title: 'Where to get help and support',
    subtitle: 'Who to contact if you need help with your finances',
    content: `**This article explains where to get support if you need help with your finances.**

## **What IE Hub can help with**

IE Hub helps you:

* Create your budget  
* Share your budget with companies

## **What IE Hub cannot do**

**IE Hub cannot provide financial advice or make decisions about your account.**

## **Who you should contact**

For account-related queries:

* Contact IE Hub support

For financial help:

* Contact the company directly  
* Or speak to a support organisation

## **Free support organisations**

You can contact:

* National Debtline  
* StepChange Debt Charity

They offer free, confidential advice.

## **What to do if you need urgent help**

* Contact the company directly  
* Speak to a support organisation as soon as possible`,
  },
];

export const SEARCH_CONFIG: SearchConfig = {
  placeholderText: 'Search for help\u2026',
  popularSearchTags: ['Log in', 'Share budget', 'Payment plan', 'Upload documents', 'Contact'],
  noResultsMessage: 'We couldn\'t find anything matching your search. Try different keywords or browse topics below.',
  synonymMap: {
    'password': 'login link',
    'login problem': 'log in',
    'send budget': 'share budget',
    'email budget': 'download or email budget',
    'proof': 'documents',
    'help': 'support',
  },
};

export const ESCALATION_CONFIG: EscalationConfig = {
  preCTACopy: 'Still need help?',
  ctaButtonText: 'Contact support',
  channel: 'Email support',
  availability: "We'll respond within 72 hours during our opening times. Operating hours are Monday - Thursday 9am-6pm and Friday 9am-5pm.",
  outOfHoursMessage: 'Your message has been received. We\'ll respond as soon as possible during working hours.',
};

export const FEEDBACK_CONFIG: FeedbackConfig = {
  promptText: 'Was this helpful?',
  positiveOption: 'Yes',
  negativeOption: 'No',
  followUpOnNo: 'Highlight contact support option',
};

export function getCategoryById(id: string): SupportCategory | undefined {
  return CATEGORIES.find(c => c.id === id);
}

export function getFaqById(id: string): SupportFaq | undefined {
  return FAQS.find(f => f.id === id);
}

export function getArticleById(id: string): SupportArticle | undefined {
  return ARTICLES.find(a => a.id === id);
}

export function getFaqsByCategory(categoryId: string): SupportFaq[] {
  return FAQS.filter(f => f.categoryId === categoryId);
}

/**
 * Returns the authoritative ordered FAQ list for a category based on the
 * Section Inventory (Deliverable 3). Matches each inventory title to an
 * existing FAQ detail page by exact title (case-insensitive, question-mark
 * normalized). Items without a matching detail page are omitted.
 */
const normalizeTitle = (s: string): string =>
  s.toLowerCase().replace(/[\u2013\u2014]/g, '-').replace(/\?+$/, '').trim();

export function getInventoryFaqs(categoryId: string): SupportFaq[] {
  const category = getCategoryById(categoryId);
  if (!category) return [];

  const categoryFaqs = getFaqsByCategory(categoryId);
  const result: SupportFaq[] = [];
  const usedIds = new Set<string>();
  for (const inventoryTitle of category.faqList) {
    const normalizedInventory = normalizeTitle(inventoryTitle);
    const match = categoryFaqs.find(
      f => !usedIds.has(f.id) && normalizeTitle(f.title) === normalizedInventory
    );
    if (match) {
      result.push(match);
      usedIds.add(match.id);
    }
  }
  return result;
}

/**
 * Returns FAQs in this category that are NOT in the Section Inventory list
 * (secondary / supporting FAQs per Gemma's guidance). Dedupes by question text
 * so we don't show two FAQs with the same question.
 */
export function getSecondaryFaqs(categoryId: string): SupportFaq[] {
  const category = getCategoryById(categoryId);
  if (!category) return [];

  const primary = getInventoryFaqs(categoryId);
  const primaryIds = new Set(primary.map(f => f.id));
  const primaryTitles = new Set(primary.map(f => normalizeTitle(f.title)));

  const seen = new Set<string>(primaryTitles);
  const result: SupportFaq[] = [];
  for (const f of getFaqsByCategory(categoryId)) {
    if (primaryIds.has(f.id)) continue;
    const key = normalizeTitle(f.title);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(f);
  }
  return result;
}

export function getHomeFaqs(): SupportFaq[] {
  return FAQS
    .filter(f => f.showOnHome)
    .sort((a, b) => (a.homeDisplayOrder ?? 99) - (b.homeDisplayOrder ?? 99));
}

export function getRelatedFaqs(faqId: string): SupportFaq[] {
  const faq = getFaqById(faqId);
  if (!faq) return [];
  return faq.relatedFaqIds
    .map(id => getFaqById(id))
    .filter((f): f is SupportFaq => f !== undefined);
}

export function getLearnMoreArticles(faqId: string): (SupportArticle & { linkTitle: string })[] {
  const faq = getFaqById(faqId);
  if (!faq) return [];
  return faq.learnMore
    .map(lm => {
      const article = getArticleById(lm.articleId);
      if (!article) return null;
      return { ...article, linkTitle: lm.title };
    })
    .filter((a): a is SupportArticle & { linkTitle: string } => a !== null);
}

export function getArticleRelatedFaqs(articleId: string): SupportFaq[] {
  return FAQS.filter(f => f.learnMore.some(lm => lm.articleId === articleId));
}

export function searchFaqs(query: string): SupportFaq[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return [];

  let resolvedQuery = lowerQuery;
  for (const [synonym, replacement] of Object.entries(SEARCH_CONFIG.synonymMap)) {
    if (lowerQuery.includes(synonym)) {
      resolvedQuery = lowerQuery.replace(synonym, replacement);
    }
  }

  return FAQS.filter(faq => {
    const titleLower = faq.title.toLowerCase();
    const contentLower = faq.content.toLowerCase();

    if (titleLower.includes(resolvedQuery)) return true;
    if (contentLower.includes(resolvedQuery)) return true;
    if (faq.searchKeywords.some(kw => kw.includes(resolvedQuery) || resolvedQuery.includes(kw))) return true;

    if (resolvedQuery !== lowerQuery) {
      if (titleLower.includes(lowerQuery)) return true;
      if (contentLower.includes(lowerQuery)) return true;
    }

    return false;
  }).slice(0, 10);
}
